// pages/reset-password.js
import { useEffect, useMemo, useState } from 'react';
import {
  confirmPasswordReset,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { Link, useLocation } from 'react-router-dom';
import { auth } from '../firebase-client';
import PasswordInput from '../components/forms/formComponents/PasswordInput';
import { passwordMeetsRequirements } from '../utils/passwordValidation';

const RESET_REQUEST_MESSAGE = "If an account exists for that email, we'll send a reset link.";
const INVALID_RESET_LINK_MESSAGE = 'This password reset link has expired or is no longer valid. Please request a new reset link.';
const UNSUPPORTED_ACTION_MESSAGE = 'This account link cannot be handled here. Please return to Member Nest and try again.';

function getResetActionCodeSettings() {
  const origin = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : '';

  return {
    url: `${origin}/login`,
    handleCodeInApp: false,
  };
}

function AuthCard({ children }) {
  return (
    <div className="card" style={{ maxWidth: '520px', marginInline: 'auto' }}>
      <h1 className="section-title">Reset your password</h1>
      {children}
    </div>
  );
}

export default function ResetPasswordPage() {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const mode = params.get('mode') || '';
  const actionCode = params.get('oobCode') || '';
  const isResetAction = mode === 'resetPassword' && Boolean(actionCode);
  const isUnsupportedAction = Boolean(mode) && mode !== 'resetPassword';

  const [email, setEmail] = useState('');
  const [requestMessage, setRequestMessage] = useState(null);
  const [requestError, setRequestError] = useState(null);
  const [requestSending, setRequestSending] = useState(false);

  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [verificationStatus, setVerificationStatus] = useState(isResetAction ? 'checking' : 'idle');
  const [verificationError, setVerificationError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetComplete, setResetComplete] = useState(false);
  const [resetSaving, setResetSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!isResetAction) {
      setVerificationStatus('idle');
      setVerifiedEmail('');
      setVerificationError('');
      return () => {
        cancelled = true;
      };
    }

    setVerificationStatus('checking');
    setVerifiedEmail('');
    setVerificationError('');
    setResetError('');
    setResetComplete(false);

    verifyPasswordResetCode(auth, actionCode)
      .then((accountEmail) => {
        if (cancelled) return;
        setVerifiedEmail(accountEmail || '');
        setVerificationStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setVerificationError(INVALID_RESET_LINK_MESSAGE);
        setVerificationStatus('invalid');
      });

    return () => {
      cancelled = true;
    };
  }, [actionCode, isResetAction]);

  const handleResetRequest = async (e) => {
    e.preventDefault();
    setRequestMessage(null);
    setRequestError(null);
    setRequestSending(true);

    try {
      await sendPasswordResetEmail(auth, email.trim().toLowerCase(), getResetActionCodeSettings());
      setEmail('');
      setRequestMessage(RESET_REQUEST_MESSAGE);
    } catch (err) {
      if (err?.code === 'auth/invalid-email') {
        setRequestError('Enter a valid email address.');
      } else {
        setRequestMessage(RESET_REQUEST_MESSAGE);
      }
    } finally {
      setRequestSending(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setResetError('');

    if (!passwordMeetsRequirements(newPassword)) {
      setResetError('Choose a password that meets the requirements below.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match.');
      return;
    }

    setResetSaving(true);
    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      setResetComplete(true);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err?.code === 'auth/weak-password') {
        setResetError('Choose a stronger password that meets the requirements below.');
      } else {
        setResetError(INVALID_RESET_LINK_MESSAGE);
      }
    } finally {
      setResetSaving(false);
    }
  };

  const renderRequestForm = () => (
    <>
      <p className="section-text" style={{ marginBottom: '1.25rem' }}>
        Enter the email address for your account and we will send a secure reset link.
      </p>
      <form onSubmit={handleResetRequest} className="flex-column" style={{ gap: '1rem' }} autoComplete="on">
        <div className="flex-column" style={{ gap: '0.35rem' }}>
          <label htmlFor="reset-email" style={{ fontWeight: 600 }}>Email</label>
          <input
            id="reset-email"
            type="email"
            name="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            inputMode="email"
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            className="form-input"
          />
        </div>
        <button type="submit" className="button" disabled={requestSending}>
          {requestSending ? 'Sending...' : 'Send reset link'}
        </button>
        {requestMessage && <p style={{ color: '#166534', margin: 0 }}>{requestMessage}</p>}
        {requestError && <p style={{ color: '#991b1b', margin: 0 }}>{requestError}</p>}
      </form>
      <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
        Return to <Link to="/login">login</Link>
      </p>
    </>
  );

  const renderActionContent = () => {
    if (isUnsupportedAction) {
      return (
        <>
          <p style={{ color: '#991b1b', marginTop: 0 }}>{UNSUPPORTED_ACTION_MESSAGE}</p>
          <p style={{ marginBottom: 0 }}>
            Return to <Link to="/login">login</Link>
          </p>
        </>
      );
    }

    if (mode === 'resetPassword' && !actionCode) {
      return (
        <>
          <p style={{ color: '#991b1b', marginTop: 0 }}>{INVALID_RESET_LINK_MESSAGE}</p>
          <p style={{ marginBottom: 0 }}>
            <Link to="/reset-password">Request a new reset link</Link>
          </p>
        </>
      );
    }

    if (verificationStatus === 'checking') {
      return <p style={{ marginBottom: 0 }}>Checking your reset link...</p>;
    }

    if (verificationStatus === 'invalid') {
      return (
        <>
          <p style={{ color: '#991b1b', marginTop: 0 }}>{verificationError}</p>
          <p style={{ marginBottom: 0 }}>
            <Link to="/reset-password">Request a new reset link</Link>
          </p>
        </>
      );
    }

    if (resetComplete) {
      return (
        <>
          <p style={{ color: '#166534', marginTop: 0 }}>
            Your password has been updated. You can now log in with your new password.
          </p>
          <Link to="/login" className="button">Go to login</Link>
        </>
      );
    }

    return (
      <>
        <p className="section-text" style={{ marginBottom: '1.25rem' }}>
          Choose a new password for {verifiedEmail || 'your Member Nest account'}.
        </p>
        <form onSubmit={handleConfirmReset} className="flex-column" style={{ gap: '1rem' }} autoComplete="on">
          <PasswordInput
            inputId="new-password"
            label="New password"
            name="newPassword"
            autoComplete="new-password"
            placeholder="New password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            showRequirements
            confirmValue={confirmPassword}
            onConfirmChange={(event) => setConfirmPassword(event.target.value)}
            confirmInputId="confirm-new-password"
            confirmLabel="Confirm new password"
            confirmName="confirmNewPassword"
            confirmAutoComplete="new-password"
            confirmPlaceholder="Confirm new password"
            disabled={resetSaving}
          />
          <button type="submit" className="button" disabled={resetSaving}>
            {resetSaving ? 'Updating...' : 'Update password'}
          </button>
          {resetError && <p style={{ color: '#991b1b', margin: 0 }}>{resetError}</p>}
        </form>
      </>
    );
  };

  return (
    <div className="page">
      <section className="section">
        <AuthCard>
          {isResetAction || isUnsupportedAction || mode === 'resetPassword'
            ? renderActionContent()
            : renderRequestForm()}
        </AuthCard>
      </section>
    </div>
  );
}

export { INVALID_RESET_LINK_MESSAGE, RESET_REQUEST_MESSAGE, getResetActionCodeSettings };
